import { useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin, TrendingUp, Clock, BadgeCheck, Sparkles, Flame } from 'lucide-react';

/**
 * Enhanced Property Card Component
 * Features: Image carousel, trust badges, progress bar, key metrics
 */
export default function PropertyCard({ property, onClick }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  // Default fallback image
  const defaultImage = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80";
  
  // Get images array or use single image/default
  const images = property.images?.length > 0 
    ? property.images 
    : property.image 
      ? [property.image] 
      : [defaultImage];

  const formatINR = (val) => {
    if (!val || val === 0) return '₹0';
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val / 100000).toFixed(2)} L`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (e, index) => {
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  // Get badge icon and color
  const getBadgeConfig = (badge) => {
    const configs = {
      'Premium': { icon: Sparkles, bg: 'bg-purple-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
      'High ROI': { icon: TrendingUp, bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' },
      'RERA Verified': { icon: BadgeCheck, bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' },
      'New Launch': { icon: Sparkles, bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
      'Almost Funded': { icon: Flame, bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
    };
    return configs[badge] || { icon: BadgeCheck, bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' };
  };

  const fundingPercentage = property.financial?.fundingPercentage || property.fundingPercentage || 0;
  const badges = property.badges || [];
  const expectedROI = property.expectedROI || '35-40%';
  const expectedTenure = property.expectedTenure || '2-3 years';
  const minInvestment = property.financial?.minInvestment || property.minInv || 500000;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      className="border border-white/10 hover:border-[#4ADE80]/50 transition-all text-left group bg-[#0A0A0A] overflow-hidden w-full"
      data-testid={`property-card-${property.id || property.projectId}`}
    >
      {/* Image Carousel */}
      <div className="relative h-52 overflow-hidden">
        {/* Current Image */}
        <img 
          src={images[currentImageIndex]} 
          alt={property.name} 
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {/* Navigation Arrows (show on hover if multiple images) */}
        {images.length > 1 && isHovering && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 backdrop-blur-sm hover:bg-black/80 flex items-center justify-center transition-all"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 backdrop-blur-sm hover:bg-black/80 flex items-center justify-center transition-all"
              aria-label="Next image"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </>
        )}
        
        {/* Dot Indicators (if multiple images) */}
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={(e) => goToImage(e, index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentImageIndex 
                    ? 'bg-white w-4' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
        
        {/* Unit Count Badge */}
        <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm px-2.5 py-1 text-xs text-white font-mono">
          {property.unitCount || 1} Unit{(property.unitCount || 1) !== 1 ? 's' : ''}
        </div>
        
        {/* Location Badge (bottom left) */}
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-black/70 backdrop-blur-sm px-2.5 py-1 text-xs text-white">
          <MapPin className="w-3 h-3 text-[#4ADE80]" />
          <span>{property.location}{property.state ? `, ${property.state}` : ''}</span>
        </div>
      </div>
      
      {/* Card Content */}
      <div className="p-5">
        {/* Trust Badges */}
        {badges.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {badges.slice(0, 3).map((badge) => {
              const config = getBadgeConfig(badge);
              const BadgeIcon = config.icon;
              return (
                <span 
                  key={badge}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs border ${config.bg} ${config.text} ${config.border}`}
                >
                  <BadgeIcon className="w-3 h-3" />
                  {badge}
                </span>
              );
            })}
          </div>
        )}
        
        {/* Property Name */}
        <h3 className="text-lg text-white font-medium mb-1 group-hover:text-[#4ADE80] transition-colors">
          {property.name}
        </h3>
        
        {/* Sector/Location */}
        <p className="text-sm text-gray-500 mb-4">
          {property.sector}{property.sector && property.location ? ', ' : ''}{property.location}
        </p>
        
        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="text-gray-500">Funding Progress</span>
            <span className="text-[#4ADE80] font-mono">{fundingPercentage.toFixed(0)}% Funded</span>
          </div>
          <div className="h-2 bg-white/10 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#4ADE80] to-[#22C55E] transition-all duration-500"
              style={{ width: `${Math.min(fundingPercentage, 100)}%` }}
            />
          </div>
        </div>
        
        {/* Key Metrics Row */}
        <div className="grid grid-cols-3 gap-2 mb-4 py-3 border-y border-white/5">
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1">Min Invest</p>
            <p className="font-mono text-sm text-white">{formatINR(minInvestment)}</p>
          </div>
          <div className="text-center border-x border-white/5">
            <p className="text-xs text-gray-600 mb-1 flex items-center justify-center gap-1">
              <TrendingUp className="w-3 h-3" /> ROI
            </p>
            <p className="font-mono text-sm text-[#4ADE80]">{expectedROI}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-600 mb-1 flex items-center justify-center gap-1">
              <Clock className="w-3 h-3" /> Tenure
            </p>
            <p className="font-mono text-sm text-white">{expectedTenure}</p>
          </div>
        </div>
        
        {/* Unit Value & CTA */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-600">Unit Value</p>
            <p className="font-mono text-lg text-white">{formatINR(property.financial?.unitValue || property.value)}</p>
          </div>
          <div className="text-xs text-[#4ADE80] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            View Details
            <ChevronRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </button>
  );
}
